import React, { FC, Fragment } from 'react';
import _map from 'lodash/map';
import _some from 'lodash/some';
import { useTranslation } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import SearchInput from '@minecraft.search-input';
import { DrawerProps, NavigationCategory } from '../shared/types';
import { useDrawerToggleCategory, useDrawerSearchFilterNameOrCategory } from '../shared/hooks';
import {
  DrawerNavigationContainer,
  DrawerNavigationAdditionalControls,
  DrawerNavigationSectionTitle,
  DrawerNavigationCategoryButton,
  ChevronIcon,
} from './DrawerNavigationStyles';

interface Props {
  submitClick: DrawerProps['submitClick'];
  renderAdditionalNavigationControls: DrawerProps['renderAdditionalNavigationControls'];
}

const DrawerNavigation: FC<Props> = ({ submitClick, renderAdditionalNavigationControls }) => {
  const { t } = useTranslation();
  const { lessSm } = WindowWidthContext.useWidthData();

  const { activeCategory, activeWithSelectionCategories, navigationSections, toggleCategory } =
    useDrawerToggleCategory();
  const { handleSearchValueChange } = useDrawerSearchFilterNameOrCategory();

  const handleCategoryChange = (category: NavigationCategory): void => {
    toggleCategory(category);
    submitClick();
  };

  return (
    <DrawerNavigationContainer data-testid="drawer-categories-wrapper" className="cn_atom_px-4 cn_atom_py-6 cn_w-full">
      {!lessSm && (
        <SearchInput
          debouncedOnChange
          name="drawer-search-input"
          placeholder={t('common:filter.searchFilterOrCategory')}
          onChange={handleSearchValueChange}
        />
      )}
      {renderAdditionalNavigationControls && (
        <DrawerNavigationAdditionalControls>{renderAdditionalNavigationControls()}</DrawerNavigationAdditionalControls>
      )}
      {_map(navigationSections, (section) => (
        <Fragment key={`section-${section.value}`}>
          {section.label && <DrawerNavigationSectionTitle>{section.label}</DrawerNavigationSectionTitle>}
          {_map(section.options, (category) => (
            <DrawerNavigationCategoryButton
              $isActive={activeCategory.value === category.value}
              $isActiveWithSelection={_some(activeWithSelectionCategories, { value: category.value })}
              key={category.value}
              data-testid={`drawer-navigation-category-${category.value}`}
              type="button"
              onClick={(): void => handleCategoryChange(category)}
            >
              {category.label}
              <ChevronIcon inheritColor name="chevron-open-thick" />
            </DrawerNavigationCategoryButton>
          ))}
        </Fragment>
      ))}
    </DrawerNavigationContainer>
  );
};

export default DrawerNavigation;
